import {
  AdditiveBlending,
  BufferGeometry,
  Float32BufferAttribute,
  Points,
  ShaderMaterial,
  TextureLoader,
  Vector3,
  Color,
} from "three";
import { default as dot } from "@assets/textures/star_64.png";
import { COLOR_GOLD_RGB, COLOR_METALLIC_BLUE_RGB } from "../../utils/constants";

export class ElectronSwarm extends Points {
  private particleCount = 150;
  private positions: Float32Array;
  private colors: Float32Array;
  private speeds: Float32Array;
  private offsets: Float32Array;
  private axes: Float32Array;
  private radii: Float32Array;
  private sizes: Float32Array;
  private loader = new TextureLoader();

  private baseRadius = 50;
  private currentCenter = new Vector3(0, 0, 1500);
  private currentScale = 1;
  private smoothScrollExpansion = 1;
  private previousTargetCenter = new Vector3(0, 0, 1500);
  private horizontalOffsets: Float32Array;
  private targetHorizontalOffsets: Float32Array;
  private lastAngleChange: Float32Array;
  private currentOrbitWidth = 1200;
  private targetOrbitWidth = 1200;
  private isMobile = false;
  private settledTime = 0;
  private currentOpacity = 1;
  private isSettled = false;
  private menuFadeTarget = 1; // 1 = visible, 0 = hidden
  private menuFadeStartTime = 0;
  private menuFadeDelay = 0;

  constructor() {
    super();

    // Detect mobile early for size calculations
    this.isMobile = window.innerWidth < 768;

    this.positions = new Float32Array(this.particleCount * 3);
    this.colors = new Float32Array(this.particleCount * 3);
    this.speeds = new Float32Array(this.particleCount);
    this.offsets = new Float32Array(this.particleCount);
    this.axes = new Float32Array(this.particleCount * 3);
    this.radii = new Float32Array(this.particleCount);
    this.sizes = new Float32Array(this.particleCount);
    this.horizontalOffsets = new Float32Array(this.particleCount);
    this.targetHorizontalOffsets = new Float32Array(this.particleCount);
    this.lastAngleChange = new Float32Array(this.particleCount);
    const randoms = new Float32Array(this.particleCount);

    const color1 = new Color(
      COLOR_GOLD_RGB.r,
      COLOR_GOLD_RGB.g,
      COLOR_GOLD_RGB.b,
    );
    const color2 = new Color(
      COLOR_METALLIC_BLUE_RGB.r,
      COLOR_METALLIC_BLUE_RGB.g,
      COLOR_METALLIC_BLUE_RGB.b,
    );

    for (let i = 0; i < this.particleCount; i++) {
      this.speeds[i] = 0.5 + Math.random() * 1.5;
      this.offsets[i] = Math.random() * Math.PI * 2;
      this.radii[i] = this.baseRadius * (0.3 + Math.random() * 2.5);
      // Mobile: 10-30, Desktop: 20-40
      const baseSize = this.isMobile ? 10 : 20;
      const sizeRange = 20;
      this.sizes[i] = baseSize + Math.random() * sizeRange;
      randoms[i] = Math.random();

      // Initialize horizontal offset within orbit width
      const effectiveRadius = this.radii[i] ?? this.baseRadius;
      const maxOffset = (this.currentOrbitWidth - 2 * effectiveRadius) / 2;
      this.horizontalOffsets[i] = (Math.random() - 0.5) * 2 * maxOffset;
      this.targetHorizontalOffsets[i] = this.horizontalOffsets[i] ?? 0;
      this.lastAngleChange[i] = Math.random() * Math.PI * 2;

      // Random color mix
      const mixedColor = color1.clone().lerp(color2, Math.random());
      this.colors[i * 3] = mixedColor.r;
      this.colors[i * 3 + 1] = mixedColor.g;
      this.colors[i * 3 + 2] = mixedColor.b;

      // Random axis for orbit
      const axis = new Vector3(
        Math.random() - 0.5,
        Math.random() - 0.5,
        Math.random() - 0.5,
      ).normalize();
      this.axes[i * 3] = axis.x;
      this.axes[i * 3 + 1] = axis.y;
      this.axes[i * 3 + 2] = axis.z;
    }

    this.geometry = new BufferGeometry();
    this.geometry.setAttribute(
      "position",
      new Float32BufferAttribute(this.positions, 3),
    );
    this.geometry.setAttribute(
      "color",
      new Float32BufferAttribute(this.colors, 3),
    );
    this.geometry.setAttribute(
      "aSize",
      new Float32BufferAttribute(this.sizes, 1),
    );
    this.geometry.setAttribute(
      "aRandom",
      new Float32BufferAttribute(randoms, 1),
    );

    const texture = this.loader.load(dot);
    this.material = new ShaderMaterial({
      uniforms: {
        map: { value: texture },
        uPixelRatio: { value: window.devicePixelRatio },
        uTime: { value: 0 },
        uGlobalOpacity: { value: 1.0 },
      },
      vertexShader: `
        uniform float uPixelRatio;
        uniform float uTime;
        attribute float aSize;
        attribute float aRandom;
        attribute vec3 color;
        varying vec3 vColor;
        varying float vAlpha;
        void main() {
          vColor = color;
          
          // Twinkle effect
          vAlpha = 0.5 + 0.5 * sin(uTime * 5.0 + aRandom * 10.0);
          
          vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
          gl_PointSize = aSize * uPixelRatio * (1000.0 / -mvPosition.z);
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        uniform sampler2D map;
        uniform float uGlobalOpacity;
        varying vec3 vColor;
        varying float vAlpha;
        void main() {
          vec4 texColor = texture2D(map, gl_PointCoord);
          gl_FragColor = vec4(vColor, vAlpha * uGlobalOpacity) * texColor;
        }
      `,
      transparent: true,
      depthWrite: false,
      blending: AdditiveBlending,
    });

    // Initial position
    this.position.set(0, 0, 0);
    this.frustumCulled = false;

    // Detect mobile
    this.isMobile = window.innerWidth < 768;
    window.addEventListener("resize", () => {
      this.isMobile = window.innerWidth < 768;
    });
  }

  public setMenuOpen(isOpen: boolean, time: number): void {
    if (isOpen) {
      // Hide immediately
      this.menuFadeTarget = 0;
      this.menuFadeDelay = 0;
      this.menuFadeStartTime = time;
    } else {
      // Fade in after 500ms delay
      this.menuFadeTarget = 1;
      this.menuFadeDelay = 0.5;
      this.menuFadeStartTime = time;
    }
  }

  public update(
    time: number,
    targetCenter: Vector3,
    scrollVelocity: number = 0,
    cameraRight: Vector3 = new Vector3(1, 0, 0),
    targetWidth: number = 1200,
  ): void {
    // Smooth orbit width transitions
    this.targetOrbitWidth = targetWidth;
    this.currentOrbitWidth +=
      (this.targetOrbitWidth - this.currentOrbitWidth) * 0.1;

    // Calculate speed of mouse movement (based on target center change)
    const movement = targetCenter.distanceTo(this.previousTargetCenter);
    this.previousTargetCenter.copy(targetCenter);

    // Smoothly move current center to target (lag effect)
    this.currentCenter.lerp(targetCenter, 0.05);

    // Calculate target scale based on movement speed
    // Mobile: reduce expansion by 50%
    const speedFactor = movement * 0.5;
    const maxExpansion = this.isMobile ? 3.0 : 4.0; // Mobile: 4x max, Desktop: 5x max
    const targetScale = 1 + Math.min(speedFactor, maxExpansion);

    // Smoothly interpolate scale
    this.currentScale += (targetScale - this.currentScale) * 0.02;

    // Add scroll velocity influence with threshold and smooth interpolation
    const velocityThreshold = 30;
    const velocityScale = Math.min(scrollVelocity / velocityThreshold, 1.0);
    const scrollExpansionMultiplier = this.isMobile ? 0.5 : 2.0; // Mobile: 1.5x max, Desktop: 3x max
    const targetScrollExpansion = 1 + velocityScale * scrollExpansionMultiplier;

    // Smooth the scroll expansion with slower easing for less abrupt transitions
    this.smoothScrollExpansion +=
      (targetScrollExpansion - this.smoothScrollExpansion) * 0.08;

    // Pulse effect (small percentage)
    const pulse = 1 + Math.sin(time * 3) * 0.1; // +/- 10% pulse

    const finalScale = this.currentScale * pulse * this.smoothScrollExpansion;

    const positionAttribute = this.geometry.attributes["position"];
    if (!positionAttribute) return;

    const positions = positionAttribute.array as Float32Array;

    for (let i = 0; i < this.particleCount; i++) {
      const speed = this.speeds[i] || 1;
      const offset = this.offsets[i] || 0;
      const angle = time * speed + offset;
      const radius = (this.radii[i] || this.baseRadius) * finalScale;

      // Change horizontal offset when particle completes a full orbit
      const currentAngle = angle % (Math.PI * 2);

      // Detect orbit completion (angle wrapped around)
      const lastAngle = this.lastAngleChange[i] ?? 0;
      if (Math.abs(currentAngle - lastAngle) > Math.PI) {
        // Pick new random horizontal offset
        const effectiveRadius = radius;
        const maxOffset = (this.currentOrbitWidth - 2 * effectiveRadius) / 2;
        this.targetHorizontalOffsets[i] = (Math.random() - 0.5) * 2 * maxOffset;
        this.lastAngleChange[i] = currentAngle;
      }

      // Smoothly interpolate to target offset
      const currentOffset = this.horizontalOffsets[i] ?? 0;
      const targetOffset = this.targetHorizontalOffsets[i] ?? 0;
      this.horizontalOffsets[i] =
        currentOffset + (targetOffset - currentOffset) * 0.05;
      // Retrieve axis
      const axis = new Vector3(
        this.axes[i * 3],
        this.axes[i * 3 + 1],
        this.axes[i * 3 + 2],
      );

      // Create a vector orthogonal to axis to define the orbit plane
      let vec1 = new Vector3(1, 0, 0);
      if (Math.abs(axis.x) > 0.9) vec1 = new Vector3(0, 1, 0);
      vec1.cross(axis).normalize();
      const vec2 = new Vector3().copy(axis).cross(vec1).normalize();

      // Use camera right vector for horizontal offset (stays aligned with camera)

      // Calculate position on the orbit ring with horizontal offset
      // P = center + horizontalOffset + radius * (cos(angle) * vec1 + sin(angle) * vec2)
      const horizontalOffset = this.horizontalOffsets[i] ?? 0;
      const p = new Vector3()
        .copy(this.currentCenter)
        .addScaledVector(cameraRight, horizontalOffset)
        .addScaledVector(vec1, Math.cos(angle) * radius)
        .addScaledVector(vec2, Math.sin(angle) * radius);

      positions[i * 3] = p.x;
      positions[i * 3 + 1] = p.y;
      positions[i * 3 + 2] = p.z;
    }

    if (this.geometry && this.geometry.attributes["position"]) {
      this.geometry.attributes["position"].needsUpdate = true;
    }

    // Update time uniform
    if (this.material) {
      const shaderMaterial = this.material as ShaderMaterial;
      if (shaderMaterial.uniforms && shaderMaterial.uniforms["uTime"]) {
        shaderMaterial.uniforms["uTime"].value = time;
      }

      // Handle mobile opacity fade when settled
      if (this.isMobile && shaderMaterial.uniforms["uGlobalOpacity"]) {
        // Handle menu fade first (takes priority)
        let menuOpacityMultiplier = 1;
        const timeSinceMenuChange = time - this.menuFadeStartTime;

        if (this.menuFadeTarget === 0) {
          // Fade out immediately
          menuOpacityMultiplier = Math.max(0, 1 - timeSinceMenuChange / 0.2);
        } else if (this.menuFadeTarget === 1 && this.menuFadeStartTime > 0) {
          // Fade in after delay
          if (timeSinceMenuChange < this.menuFadeDelay) {
            menuOpacityMultiplier = 0;
          } else {
            // Fade in over 500ms
            const fadeProgress = Math.min(
              (timeSinceMenuChange - this.menuFadeDelay) / 0.5,
              1,
            );
            menuOpacityMultiplier = fadeProgress;
          }
        }

        // Check if particles are settled (no significant movement or scroll)
        const isCurrentlySettled = movement < 1 && scrollVelocity < 5;

        if (isCurrentlySettled) {
          if (!this.isSettled) {
            this.settledTime = time;
            this.isSettled = true;
          }
          // Wait 0.5 seconds before starting to fade
          const timeSinceSettled = time - this.settledTime;
          if (timeSinceSettled > 0.5) {
            // Fade to 50% opacity over 0.5 seconds
            const fadeProgress = Math.min((timeSinceSettled - 0.5) / 0.5, 1);
            this.currentOpacity = 1 - fadeProgress * 0.5; // 1 -> 0.5
          }
        } else {
          this.isSettled = false;
          // Quickly restore full opacity when moving
          this.currentOpacity = Math.min(this.currentOpacity + 0.1, 1);
        }

        shaderMaterial.uniforms["uGlobalOpacity"].value =
          this.currentOpacity * menuOpacityMultiplier;
      } else if (shaderMaterial.uniforms["uGlobalOpacity"]) {
        // Always full opacity on desktop
        shaderMaterial.uniforms["uGlobalOpacity"].value = 1.0;
      }
    }
  }
}
